/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-invalid-this */
/**
 * Transfer Group service
 */
import { Injectable, Injector, OnInit } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  ColDef,
  ValueFormatterParams,
  RowDoubleClickedEvent,
} from 'ag-grid-community';
import {
  chain,
  concat,
  filter,
  find,
  flow,
  get,
  head,
  includes,
  isEmpty,
  isEqual,
  isNil,
  map,
  merge,
  orderBy,
  reduce,
  set,
  size,
  sortBy,
  toLower,
  pick,
  partition,
  isFunction,
} from 'lodash';

import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import {
  InventoryItemVenueMeasure,
  Threshold,
} from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue';

import { wrapWithLoader } from '@app/shared/common/decorators';
import { setFormFieldValue } from '@app/components/form/services/schema-form/schema-form-functions';
import { ISitePreference } from '@app/shared/types/preferences/preferences.type';
import { DecimalNumber } from '@app/shared/common';
import { TRANSFER_GROUP_ROUTER_REGEX } from '@app/containers/transfer-group/transfer-group.constants';
import { XuxGridListComponent } from '@app/components/xux/xux-grid-list/xux-grid-list-widget.component';
import {
  CELL_FILTER,
  CELL_RENDERER,
  COLUMN_SELECTOR_ID,
  ENTITY_TYPES,
  FilterItemsSourceEnum,
  FilterOptionType,
  IApiListResponse,
  IBaseEntityResponse,
  IBaseEntityType,
  IBulkUpdateDocumentsResult,
  IChangeStatusTotalRes,
  IDocumentOperationOptions,
  IEntityConfig,
  IFilterQuery,
  IInventoryItem,
  IModalDialogOptions,
  IModalDialogResult,
  ISite,
  ISiteResponse,
  ITask,
  ITaskOptions,
  ITransferData,
  ITransferGroupInfo,
  IXVIListColumn,
  NotificationType,
  TASK_TYPE,
  TRANSFER_TYPE,
  XUX_GRID_ACTIONS,
  XuxGridAction,
  MEASURE_TRANSACTION,
  DID_STATUS_ACTION_MAPPING,
} from '@shared/types';
import { getSingularEntityName } from '@app/shared/configs';
import { COLUMN_ALIGN_RIGHT } from '@app/shared/constants/column-align-definition';
import {
  generateSpacesText,
  generateUnitTextV2,
  ObjectId,
  toDate,
  truncationText,
} from '@app/shared/utils';
import {
  BTN_PRIMARY_CLASS,
  ENTITY_ID_PATH,
  MAX_PAGE_SIZE,
  TIME_FORMATS,
} from '@config/global-constants';
import { lazyInject } from '@app/containers/base/decorators';
import { TRANSFER_STATUS_VALUE } from '@shared/types/enums/transfer-status.enum';
import {
  TOASTER_ERROR_MESSAGE,
  TOASTER_SUCCESS_MESSAGE,
  TRANSFER_OPERATION_STATUS_MAPPING,
} from '@shared/constants';
import { BehaviorSubject, Observable, observableOf, rx } from '@app/shared/rx';
import {
  defaultWidgetData,
  gridSelectModalOptions,
} from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.settings';
import {
  IPdfDefinitions,
  IPdfOptions,
  PdfGenerateServiceV2,
} from '@app/shared/common/make-pdf/v2';

import { groupPrintSheetModalDefinition } from './print-sheet-modal.definition';
import { buildPrintSheetPdfConfig } from '../transfer/print-sheet-pdf-config';
import { InventoryItemVenueService } from '../inventory-item-venue';
import { TransferService } from '../transfer/transfer.service';
import { NumberFormatService } from '../number-format';
import { DataSource, EntityServiceProvider } from '../datasource';
import { DmDataService } from '../dm-data';
import { CompanyUtilService } from '../company';
import { SitesService } from '../sites/sites.service';
import { XprtDataService } from '../xprt-data';
import {
  isSiteTypePrimaryWarehouse,
  setPrimaryAndWarehouseTop,
  setPrimaryAndWarehouseTopAndAddStoreNumber,
  transferTypeFormatter,
} from '../transfer/transfer.util';
import { transferEntityType } from '../transfer/transfer.service.config';
import { BaseService } from '../base';
import {
  BTN_YES,
  BTN_CANCEL,
} from '@app/text-content/button-templates/button-templates-content';
import { iif } from 'rxjs';
import { fromPromise } from 'rxjs/internal-compatibility';
import { TaskService } from '../task';
import { TRANSFER_GROUP_MODULE } from '@app/routes';
import {
  isGroupAutoRestockPage,
  buildGroupAutoRestockPagePath,
} from '@app/shared/utils/transfer-group.auto-restock.utils';
import { getTimeAtColDef } from '@shared/services/transfer/transfer-column.definition';
import { CountryFormatService } from '@app/shared/services/country-format';

import { I18n } from '@app/i18n/strings-keys';

const entityType: ENTITY_TYPES = ENTITY_TYPES.TRANSFER_GROUP;
const config: IEntityConfig = {
  ENTITY_TYPE: entityType,
  entityNameToCheckPermissions: transferEntityType,
  isMasterListAvailable: true,
  buttons: null,
  apiEndpoint: entityType,
  uiRoute: entityType,
  isFormServiceSupported: true,
  editor: {
    entityTitle: getSingularEntityName(entityType),
  },
  suppressPermissionEntityTypeUsageForNavigation: (
    event: NavigationEnd,
  ): boolean => {
    return !TRANSFER_GROUP_ROUTER_REGEX.groupChildPage.test(event.url);
  },
  needConvertTimeZoneFields: ['transaction_date'],
  requiredPermissions: ['transfer.View'],
  list: {
    userGridOptions: {
      suppressRowClickSelection: true,
    },
    gridOptions: {
      valueField: '_id',
      multiselect: false,
    },
    documentQuery: {
      orderby: 'updated_at desc',
      notNeedActive: true,
      include_cost_fields: true,
      select: [ENTITY_ID_PATH, 'site_id', 'transfer_group_type'],
    },
    documentOperationOptions: {
      skipMappers: true,
    },
    columns: [],
    async beforeFetchListData(entityService: TransferService): Promise<void> {
      const fetchSiteFromDataSourceList = async (): Promise<void> => {
        entityService.siteFromDataSource =
          await entityService.siteFromDataSourceFetchList();
      };

      await fetchSiteFromDataSourceList();
    },
  },
};

/**
 * Transfer Group service that extends BaseService to use
 * basic CRUD request operations
 */
@Injectable()
export class TransferGroupService extends BaseService implements OnInit {
  public static config: IEntityConfig = config;
  public static ENTITY_TYPE: string = config.ENTITY_TYPE;

  @lazyInject(TransferService) public transferService: TransferService;
  @lazyInject(EntityServiceProvider)
  public entityServiceProvider: EntityServiceProvider;
  @lazyInject(CompanyUtilService) public companyUtilService: CompanyUtilService;
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(PdfGenerateServiceV2)
  public pdfGenerateServiceV2: PdfGenerateServiceV2;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(TaskService) public taskService: TaskService;
  @lazyInject(CountryFormatService)
  public countryFormatService: CountryFormatService;

  public companySitesList$: BehaviorSubject<ISite[]> = new BehaviorSubject<
    ISite[]
  >([]);
  public siteModalDescription$: BehaviorSubject<string> =
    new BehaviorSubject<string>(
      this.i18nService.instant(I18n.Forms.GridMessages.ShowingEntities, {
        count: 0,
        entities: this.i18nService.translate(
          I18n.EntityNames.SiteSingularAndPlural,
        ),
      }),
    );

  public quickFilterFields: string[] = ['name'];

  public listProjection: string[] = ['name'];

  public headerNames: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      filter: false,
    },
  ];

  public transferFromDataSource: DataSource;

  public companySitesList: ISite[] = [];
  public inventoryItemGroupResponse: IBaseEntityResponse<IInventoryItem>;
  public printSheetsModalForm: FormGroup;

  public siteListSelected: number = 0;
  public siteModalTotal: number = 0;
  public siteModalSelected: number = 0;
  public summaryTitleFontSize: number = 8;

  public transferFieldsSelect: string[] = [
    'id',
    'site_department_code_to',
    'site_id_to',
    'store_number_to',
    'site_name_to',
    'status',
    'metadata',
    'request_cost',
    'reversed_cost',
    'received_cost',
    'internal_number',
    'transaction_date',
    'updated_at',
    'updated_by',
    'transfer_type',
    'updated_items',
    'unavailable_items',
  ];

  public siteFromDataSource: IApiListResponse;
  public transferAutoRestockUri: string = `/${TRANSFER_GROUP_MODULE}/auto-restock`;

  public constructor(public injector: Injector) {
    super(injector);

    const listColumns: IXVIListColumn[] = [
      {
        path: 'name',
        title: I18n.Common.Name,
        filterType: CELL_FILTER.string,
        filterParams: {
          quickFilter: true,
        },
        sortable: true,
        cellRenderer: CELL_RENDERER.entity_link_renderer,
        defaultView: true,
      },
      {
        path: 'transaction_date',
        title: I18n.Common.Date,
        filterType: CELL_FILTER.dateLocale,
        sortable: true,
        defaultView: true,
      },
      {
        path: 'site_name',
        title: I18n.Forms.TransferForm.FromNameBrackets,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType,
        },
        sortable: true,
        defaultView: true,
      },
      {
        path: 'store_number',
        title: I18n.Forms.TransferForm.FromIDBrackets,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType,
        },
        sortable: true,
        defaultView: false,
      },
      {
        path: 'transfer_type',
        title: I18n.Common.Type,
        filterType: CELL_FILTER.enum,
        filterParams: {
          values: [
            {
              text: this.i18nService.instant(
                I18n.Forms.TransferForm.PreEventHyphen,
              ),
              value: TRANSFER_TYPE.PRE_EVENT_TRANSFER,
            },
            {
              text: this.i18nService.instant(
                I18n.Forms.TransferForm.DuringEvent,
              ),
              value: TRANSFER_TYPE.DURING_EVENT_TRANSFER,
            },
            {
              text: this.i18nService.instant(
                I18n.Forms.TransferForm.OutsideofEvent,
              ),
              value: TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER,
            },
          ],
        },
        sortable: true,
        valueFormatter: (params) =>
          transferTypeFormatter(params, { i18nService: this.i18nService }),
        defaultView: true,
      },
      {
        path: 'event_id',
        title: I18n.EntityNames.Event,
        defaultView: true,
        filterType: CELL_FILTER.entity,
        filterParams: {
          default: {
            type: 'containsAny' as FilterOptionType,
          },
          filterItemsSource: FilterItemsSourceEnum.XPRT_EVENTS,
          filterOptions: ['containsAny'] as FilterOptionType[],
          filterItemSourceQuery: {
            select: ['name', 'id'],
          },
          suppressUIFiltering: true,
        },
      },
      {
        path: 'transfer_group_received_cost',
        title: I18n.Forms.TransferForm.ReceivedCost,
        sortable: false,
        defaultView: true,
        valueFormatter: (params: ValueFormatterParams): string => {
          return isEqual(
            params.data.transfer_type,
            TRANSFER_TYPE.PRE_EVENT_TRANSFER,
          )
            ? ''
            : this.numberFormatService.getNumberFormatter(true)({
                value: params.value,
              });
        },
        ...COLUMN_ALIGN_RIGHT,
      },
      {
        path: 'transfer_group_request_cost',
        title: I18n.Forms.TransferForm.RequestCost,
        sortable: false,
        defaultView: true,
        valueFormatter: (params: ValueFormatterParams): string => {
          return this.numberFormatService.getNumberFormatter(true)({
            value: params.value,
          });
        },
        ...COLUMN_ALIGN_RIGHT,
      },
      {
        path: 'transfer_group_reversed_cost',
        title: I18n.Forms.TransferForm.ReversedCost,
        sortable: false,
        defaultView: true,
        valueFormatter: (params: ValueFormatterParams): string => {
          return this.numberFormatService.getNumberFormatter(true)({
            value: params.value,
          });
        },
        ...COLUMN_ALIGN_RIGHT,
      },
      getTimeAtColDef({
        companyService: this.companyService,
        i18nService: this.i18nService,
      }),
      {
        path: COLUMN_SELECTOR_ID,
        width: 50,
        title: '',
        defaultView: true,
        cellRenderer: CELL_RENDERER.transfer_group_actions_renderer,
        pinned: detectMobile() ? false : 'right',
        lockPinned: true,
      },
    ];
    this.config.list.columns.push(...listColumns);

    this.config.list.userGridOptions = merge(
      {},
      this.config.list.userGridOptions,
      {
        onRowDoubleClicked: this.listDataDoubleClick.bind(this),
      },
    );
    this.config.buttons = {
      edit: {
        hidden: true,
      },
      auto_restock: {
        caption: I18n.Common.AutoRestock,
        hidden: this.autoRestockHidden.bind(this),
        click: this.openAutoRestock.bind(this),
      },
      print_sheets: { click: this.printSheetsBtnClickHandler },
    };
  }

  // eslint-disable-next-line @angular-eslint/contextual-lifecycle
  public ngOnInit(): void {}

  public backToFrontItemMapper(doc: ITransferGroupInfo): IBaseEntityType {
    set(
      doc,
      'virtual.transfer_items',
      orderBy(get(doc, 'transfer_items'), ['updated_at'], ['desc']),
    );

    if (isEqual(doc.transfer_type, TRANSFER_TYPE.DURING_EVENT_TRANSFER)) {
      set(doc, 'virtual.transaction_date', get(doc, 'transaction_date'));
    }

    delete doc.transfer_items;

    return flow(super.backToFrontItemMapper.bind(this))(doc);
  }

  public async listDataDoubleClick(
    event: RowDoubleClickedEvent,
  ): Promise<void> {
    const item: IBaseEntityType = event?.data;
    const id = get(item, '_id', null);

    if (id) {
      if (isGroupAutoRestockPage(item)) {
        this.uiActions.openRoute(buildGroupAutoRestockPagePath(id));

        return;
      }

      this.apiActions.openDocumentEditor(this.config.ENTITY_TYPE, id);
    }
  }

  public autoRestockHidden(): boolean {
    return !this.permissionService.canAdd(TransferService.ENTITY_TYPE);
  }

  public async openAutoRestock(): Promise<void> {
    this.uiActions.openRoute(this.transferAutoRestockUri);
  }

  public frontToBackItemMapper(doc: ITransferGroupInfo): IBaseEntityType {
    return flow(
      this.processTransactionDateValue.bind(this),
      super.frontToBackItemMapper.bind(this),
    )(doc);
  }

  public processTransactionDateValue(
    doc: ITransferGroupInfo,
  ): ITransferGroupInfo {
    return {
      ...doc,
      transaction_date:
        get(doc, 'virtual.transaction_date') || doc.transaction_date,
    };
  }

  public async bulkUpdateTransfers(
    transfers: ITransferData[],
    noLoader: boolean = false,
  ): Promise<IBulkUpdateDocumentsResult> {
    if (isEmpty(transfers)) {
      return {
        totalCount: 0,
        successCount: 0,
        updated_documents: [],
      };
    }

    if (!noLoader) {
      this.uiActions.enableLoader();
    }

    const requestFields = ['status', 'inventory_items', '_id'];

    const requestDocuments = map(transfers, (item) =>
      pick(item, requestFields),
    );

    return this.transferService
      .bulkUpdateEntity(requestDocuments, {})
      .pipe(
        rx.finalize(() => {
          if (!noLoader) {
            this.uiActions.disableLoader();
          }
        }),
      )
      .toPromise();
  }

  public bulkUpdateStatus(
    status: TRANSFER_STATUS_VALUE,
    selectedItems: ITransferData[],
    options?: ITaskOptions,
  ): Observable<
    IBulkUpdateDocumentsResult<{
      _id: ObjectId;
      status: TRANSFER_STATUS_VALUE;
    }>
  > {
    const totalCount = size(selectedItems);
    const { isSync, noLoader, currentStep, stepCount } = merge(
      { isSync: false, noLoader: false },
      options,
    );

    if (isEqual(totalCount, 0)) {
      return observableOf({
        totalCount,
        successCount: 0,
        updated_documents: [],
      });
    }

    if (!noLoader) this.uiActions.enableLoader();

    const needBatchAcknowledgeTransferIds = includes(
      [TRANSFER_STATUS_VALUE.Canceled, TRANSFER_STATUS_VALUE.Rejected],
      status,
    )
      ? map(
          filter(
            selectedItems,
            (item) =>
              !isNil(item.updated_items) || !isNil(item.unavailable_items),
          ),
          '_id',
        )
      : [];

    return iif(
      () => !isEmpty(needBatchAcknowledgeTransferIds),
      this.transferService.batchAcknowledgeDocuments(
        needBatchAcknowledgeTransferIds,
      ),
      observableOf({}),
    ).pipe(
      rx.take(1),
      rx.switchMap(() => {
        const [needUpdateTransfers, couldUpdateStatusTransfers] = partition(
          selectedItems,
          (transfer) => !isEmpty(transfer.inventory_items),
        );
        if (isEmpty(needUpdateTransfers)) {
          return observableOf(selectedItems);
        }

        // save all items first
        return fromPromise(
          this.bulkUpdateTransfers(needUpdateTransfers, true),
        ).pipe(
          rx.map((updateResult) =>
            !isEmpty(updateResult.updated_documents)
              ? concat(
                  couldUpdateStatusTransfers,
                  updateResult.updated_documents,
                )
              : couldUpdateStatusTransfers,
          ),
        );
      }),
      rx.switchMap((transfers) => {
        const transferIds = map(transfers, '_id');

        return iif(
          () => isEmpty(transferIds),
          observableOf({
            totalCount,
            successCount: 0,
            updated_documents: [],
          }),
          this.taskService
            .getBulkChangeStatusResult(
              { transferIds, isSync, toStatus: status },
              { isSync, noLoader, currentStep, stepCount },
            )
            .pipe(
              rx.map((task: ITask) => {
                const updatedItems = filter(get(task, 'sub_tasks', []), {
                  type: TASK_TYPE.UpdateTransfer,
                }).map((item) => ({
                  _id: get(item, 'value.transfer_id'),
                  status: get(item, 'value.transfer_status'),
                }));

                return {
                  totalCount,
                  successCount: updatedItems.filter(
                    (item) => item.status === status,
                  ).length,
                  updated_documents: updatedItems,
                };
              }),
            ),
        ).pipe(
          rx.catchError(() => {
            return observableOf(<IChangeStatusTotalRes>{
              successful: { total: 0, list: [] },
              failed: { total: totalCount, list: selectedItems },
            });
          }),
          rx.map((updateStatusResult: any) => {
            return {
              totalCount: updateStatusResult.totalCount,
              successCount: updateStatusResult.successCount,
              updated_documents: updateStatusResult.updated_documents,
            };
          }),
          rx.finalize(() => {
            if (!noLoader) {
              this.uiActions.disableLoader();
            }
          }),
        );
      }),
    );
  }

  public bulkVoid(
    selectedItems: ITransferData[],
    options?: ITaskOptions,
  ): Observable<IBulkUpdateDocumentsResult> {
    const totalCount = size(selectedItems);
    const { isSync, noLoader, currentStep, stepCount } = merge(
      { isSync: false, noLoader: false },
      options,
    );

    const allFailedRes = {
      totalCount,
      successCount: 0,
    };

    return this.taskService
      .getBulkChangeStatusResult(
        {
          isSync,
          transferIds: map(selectedItems, '_id'),
          toStatus: TRANSFER_STATUS_VALUE.Void,
        },
        { isSync, noLoader, currentStep, stepCount },
      )
      .pipe(
        rx.map((task: ITask) => {
          const updatedItems = filter(get(task, 'sub_tasks', []), {
            type: TASK_TYPE.UpdateTransfer,
          }).map((item) => ({
            _id: get(item, 'value.transfer_id'),
            status: get(item, 'value.transfer_status'),
          }));

          return <IBulkUpdateDocumentsResult>{
            totalCount,
            successCount: updatedItems.filter(
              (item) => item.status === TRANSFER_STATUS_VALUE.Void,
            ).length,
            updated_documents: updatedItems,
          };
        }),
        rx.catchError(() => observableOf(allFailedRes)),
      );
  }

  public async bulkVoidHandlerForGroup(
    selectedItems: ITransferData[],
    enableSingleVoid = true,
    additionOptions?: {
      checkIsCanSave?: () => Promise<boolean>;
    },
  ): Promise<IBulkUpdateDocumentsResult> {
    if (isEmpty(selectedItems)) {
      return;
    }

    const totalCount = size(selectedItems);
    const isSingleVoid = isEqual(totalCount, 1) && enableSingleVoid;
    const confirmText = isSingleVoid
      ? this.i18nService.instant(I18n.InfoMessages.VoidAdverselyImpactQOH, {
          target: this.i18nService.instant(
            I18n.Forms.TransferForm.ThisTransfer,
          ),
        })
      : this.i18nService.instant(
          I18n.InfoMessages.TransferGroupVoidAdverselyImpactQOH,
          {
            count: totalCount,
            target: this.i18nService.instant(
              I18n.Forms.TransferForm.transfersBrackets,
            ),
          },
        );

    const modalOptions: IModalDialogOptions = {
      title: I18n.Common.Confirm,
      text: confirmText,
      buttons: [
        {
          ...BTN_YES,
          class: BTN_PRIMARY_CLASS,
        },
        BTN_CANCEL,
      ],
    };
    const result = await this.uiActions.promptFormModal(modalOptions);
    if (!result.confirmed) return;

    if (isFunction(additionOptions?.checkIsCanSave)) {
      const isCanSave = await additionOptions.checkIsCanSave();

      if (!isCanSave) {
        return;
      }
    }

    const bulkUpdateResult: IBulkUpdateDocumentsResult = await this.bulkVoid(
      selectedItems,
      {
        isSync: isSingleVoid,
      },
    ).toPromise();

    if (isNil(bulkUpdateResult)) {
      return;
    }

    const successCount = get(bulkUpdateResult, 'successCount', 0);
    const failedCount = get(bulkUpdateResult, 'totalCount', 0) - successCount;

    if (failedCount > 0) {
      let errorMsg = isSingleVoid
        ? this.i18nService.instant(I18n.ErrorMessages.SingleTransferVoidFailed)
        : this.i18nService.instant(
            I18n.ErrorMessages.PartialTransferVoidFailed,
            {
              failedCount,
            },
          );

      if (successCount > 0) {
        errorMsg =
          this.i18nService.instant(I18n.InfoMessages.PartialOrAllsuccessfully, {
            successCount,
            target: this.i18nService.instant(
              I18n.Forms.TransferForm.transfersBrackets,
            ),
            did: this.i18nService.translate(I18n.Status.voided),
            trailSymbol: '. ',
          }) + errorMsg;
      }

      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: errorMsg,
      });
    } else {
      this.uiActions.showToasterMessage({
        type: NotificationType.SUCCESS,
        title: TOASTER_SUCCESS_MESSAGE.title,
        body: isSingleVoid
          ? this.i18nService.instant(I18n.InfoMessages.Didsuccessfully, {
              did: this.i18nService.instant(
                TRANSFER_OPERATION_STATUS_MAPPING[TRANSFER_STATUS_VALUE.Void],
              ),
            })
          : this.i18nService.instant(
              I18n.InfoMessages.PartialOrAllsuccessfully,
              {
                successCount,
                target: this.i18nService.instant(
                  I18n.Forms.TransferForm.transfersBrackets,
                ),
                did: this.i18nService.translate(I18n.Status.voided),
                trailSymbol: '!',
              },
            ),
      });
    }

    return bulkUpdateResult;
  }

  public shownUpdateMessage(
    msgOpts: {
      did: string;
      wantTo: string;
      isSingle: boolean;
    },
    updateResult: IBulkUpdateDocumentsResult,
  ): void {
    const { did, wantTo, isSingle } = msgOpts;

    const upperCaseDid = this.i18nService.instant(
      get(DID_STATUS_ACTION_MAPPING[did], 'upperCase', ''),
    );
    const lowerCaseDid = this.i18nService.instant(
      get(DID_STATUS_ACTION_MAPPING[did], 'lowerCase', ''),
    );

    const target = this.i18nService.instant(
      I18n.Forms.TransferForm.transfersBrackets,
    );

    const successCount = get(updateResult, 'successCount', 0);
    const failedCount = updateResult.totalCount - successCount;

    if (failedCount <= 0) {
      this.uiActions.showToasterMessage({
        type: NotificationType.SUCCESS,
        title: TOASTER_SUCCESS_MESSAGE.title,
        body: isSingle
          ? this.i18nService.instant(I18n.InfoMessages.Didsuccessfully, {
              did: upperCaseDid,
            })
          : this.i18nService.instant(
              I18n.InfoMessages.PartialOrAllsuccessfully,
              {
                target,
                did: lowerCaseDid,
                successCount: updateResult.totalCount,
                trailSymbol: '!',
              },
            ),
      });
    } else {
      let errorMsg = this.i18nService.instant(
        I18n.ErrorMessages.BulkOperationFailed,
        {
          failedCount,
          target,
          did: lowerCaseDid,
        },
      );

      if (successCount > 0) {
        errorMsg =
          this.i18nService.instant(I18n.InfoMessages.PartialOrAllsuccessfully, {
            successCount,
            target,
            did: lowerCaseDid,
            trailSymbol: '. ',
          }) + errorMsg;
      } else if (isSingle) {
        errorMsg = this.i18nService.instant(I18n.ErrorMessages.FailedToDo, {
          wantTo,
        });
      }

      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: errorMsg,
      });
    }
  }

  public printSheetsBtnClickHandler = async (): Promise<void> => {
    this.initTransferFromSource();
    this.transferFromDataSource?.refresh();
    this.companySitesList$.next([]);
    const result = await this.openPrintSheetModal();

    if (!result.confirmed) return;
    const { site_id, items } = result.value;
    try {
      // get getInventoryItemVenues and print
      await this.printInventoryItemVenues(
        find(this.companySitesList, { id: site_id }),
        items,
      );
    } catch (error) {
      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: this.i18nService.instant(
          I18n.ErrorMessages.CheckInternetAndTryAgain,
        ),
      });
    }
  };

  public initTransferFromSource(): void {
    this.transferFromDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.SITE,
    );
    this.transferFromDataSource.fetchList = (): Promise<IApiListResponse> =>
      Promise.resolve(this.siteFromDataSource);
  }

  public siteFromDataSourceFetchList(): Promise<ISiteResponse> {
    const queryWithDefaults: IFilterQuery = {
      filter: `is_active eq true`,
      select: [
        'name',
        'id',
        'type',
        'business_type',
        'store_number',
        'metadata',
      ],
      filter_by_assigned_sites: true,
      include_inactive: false,
      top: MAX_PAGE_SIZE,
    };

    return this.sitesService
      .fetchList(queryWithDefaults)
      .pipe(
        rx.first(),
        rx.map((sitesRes: ISiteResponse) => {
          this.companySitesList = sortBy(sitesRes.items, (site: ISite) =>
            toLower(site.name),
          ) as ISite[];
          sitesRes.items = setPrimaryAndWarehouseTopAndAddStoreNumber(
            this.companySitesList,
          );
          sitesRes.total = size(sitesRes.items);

          return sitesRes;
        }),
      )
      .toPromise()
      .then(
        (result) => result,
        (error) => {
          this.transferFromDataSource.error.next(error);

          return Promise.reject(error);
        },
      );
  }

  public async openPrintSheetModal(): Promise<IModalDialogResult> {
    const siteSelectorGridSettings: IModalDialogOptions =
      groupPrintSheetModalDefinition.selectSiteAndToSitesModal(this);
    const modalOptions: IModalDialogOptions = merge(
      {
        schema: {
          properties: {
            site_id: {
              type: 'string',
            },
            items: {
              type: 'array',
            },
          },
        },
      },
      defaultWidgetData,
      gridSelectModalOptions(this.companySitesList$, this.i18nService),
      siteSelectorGridSettings,
      {
        onFormReady: this.popWindowFormReady(),
      },
    );
    const dialogResult = await this.uiActions.promptFormModal(modalOptions);

    return dialogResult;
  }

  public popWindowFormReady(): Function {
    return (form: FormGroup): void => {
      this.printSheetsModalForm = form;
      setFormFieldValue(form, 'site_id', '', false);
      setFormFieldValue(form, 'items', [], false);
      form.controls.site_id.valueChanges
        .pipe(
          rx.filter(Boolean),
          rx.tap(() => {
            this.companySitesList$.next([]);
          }),
          rx.map((siteId: ObjectId) => {
            return {
              currentSite: find(this.companySitesList, ['id', siteId]),
              filteredByFromSites: filter(
                this.companySitesList,
                (site: ISite) => !isEqual(site.id, siteId),
              ),
            };
          }),
          rx.mergeMap(
            ({
              currentSite,
              filteredByFromSites,
            }: {
              currentSite: ISite;
              filteredByFromSites: ISite[];
            }) => {
              return isSiteTypePrimaryWarehouse(currentSite)
                ? this.getPrimaryWarehouseList(filteredByFromSites, currentSite)
                : Observable.from([filteredByFromSites]);
            },
          ),
        )
        .subscribe((sites: ISite[]) => {
          this.companySitesList$.next(sites);
        });

      const headElementOfSites = head(
        setPrimaryAndWarehouseTopAndAddStoreNumber(this.companySitesList),
      );
      const hasPrimaryWarehouse =
        isSiteTypePrimaryWarehouse(headElementOfSites);
      const id = hasPrimaryWarehouse ? get(headElementOfSites, 'id') : null;
      setFormFieldValue(this.printSheetsModalForm, 'site_id', id || '', true);
    };
  }

  public getPrimaryWarehouseList(
    filteredByFromSite: ISite[],
    currentSite: ISite,
  ): Observable<ISite[]> {
    const siteIds: ObjectId[] = map(filteredByFromSite, 'id');
    this.uiActions.enableModalLoader();

    return this.dmDataService.getSiteGroupId(siteIds).pipe(
      rx.switchMap((siteGroupId: string) =>
        this.dmDataService.getSitesPreferencesBySiteGroupId(siteGroupId),
      ),
      rx.mergeMap((sitePreferences: ISitePreference[]) => {
        const siteFromId: ObjectId = currentSite.id;
        const sitePrimaryWarehouseMap = reduce(
          sitePreferences,
          (result, sitePreference: ISitePreference) => {
            if (
              !isNil(get(sitePreference, 'preferences.back_office.warehouse'))
            ) {
              return {
                ...result,
                [sitePreference.site_id]: get(
                  sitePreference,
                  'preferences.back_office.warehouse.primary_warehouse',
                ),
              };
            }

            return result;
          },
          {},
        );

        return setPrimaryAndWarehouseTop(
          filter(filteredByFromSite, (site: ISite) => {
            return isEqual(get(sitePrimaryWarehouseMap, site.id), siteFromId);
          }),
        );
      }),
      rx.toArray(),
      rx.finalize(() => {
        this.uiActions.disableModalLoader();
      }),
    );
  }

  @wrapWithLoader(null, 0, true)
  public async printInventoryItemVenues(
    fromSite: ISite,
    toSites: ISite[],
  ): Promise<void> {
    this.inventoryItemGroupResponse = await this.fetchInventoryItemGroup(
      [...map(toSites, 'id'), fromSite.id],
      fromSite.id,
    );
    const pdfConfig = buildPrintSheetPdfConfig(
      async (
        definitions: IPdfDefinitions,
        bulkData: IPdfDefinitions['bulkOptions'],
      ) => {
        return this.getPrintSheetPdfData(bulkData, definitions?.options);
      },
      {
        data: map(
          sortBy(toSites, (site: ISite) => toLower(site.name)),
          (toSite: ISite) => ({
            ...toSite,
            fromSiteName: fromSite.name,
            fromSiteStoreNumber: fromSite.store_number,
            fromSiteId: fromSite.id,
          }),
        ),
      },
      this.i18nService,
    );
    await this.pdfGenerateServiceV2.printPdfAsync(pdfConfig);
  }

  public fetchList(
    userQuery?: IFilterQuery,
    options: IDocumentOperationOptions = {},
  ): Observable<any> {
    return super
      .fetchList(userQuery, { ...options, showErrorMessage: false })
      .pipe(
        rx.switchMap((res: IBaseEntityResponse) => {
          return this.xprtDataService.combineEventIntoEntityResponse(res);
        }),
      );
  }

  public fetchInventoryItemGroup(
    siteIds: ObjectId[],
    siteFromId: ObjectId,
  ): Promise<IBaseEntityResponse<IInventoryItem>> {
    const fetchInventoryItemDefaultParams = {
      masterFilter: 'measures/transactions/transfer eq in(true)',
      filter:
        'measures/transactions/transfer eq in(true) and is_master eq false',
      select: [
        'code',
        'name',
        'measures',
        'thresholds',
        'site_id',
        'is_chargeable',
      ],
      top: MAX_PAGE_SIZE,
    };

    return this.dmDataService
      .getSiteGroupIdList(siteIds)
      .pipe(
        rx.mergeMap((groupIds: string[]) =>
          this.inventoryItemVenueService.batchFetchCurrentList(
            groupIds,
            fetchInventoryItemDefaultParams,
          ),
        ),
        rx.map(
          (
            inventoryItemResponse: IBaseEntityResponse<IInventoryItem>,
          ): IBaseEntityResponse<IInventoryItem> => {
            const fromInventoryItemGroupEntityIds = chain(
              inventoryItemResponse.items,
            )
              .filter((item) => isEqual(item.site_id, siteFromId))
              .map((item) => item.entity_id)
              .value();

            const fromAndToInventoryItemGroup = filter(
              inventoryItemResponse.items,
              (item) =>
                includes(fromInventoryItemGroupEntityIds, item.entity_id),
            );

            inventoryItemResponse.items = fromAndToInventoryItemGroup;
            inventoryItemResponse.total = size(fromAndToInventoryItemGroup);

            return inventoryItemResponse;
          },
        ),
        rx.first(),
      )
      .toPromise();
  }

  public async getPrintSheetPdfData(
    baseData: IBaseEntityType,
    options: IPdfOptions = {},
  ): Promise<IBaseEntityType> {
    const document: IBaseEntityType = {
      site_name: truncationText(
        generateSpacesText(baseData.fromSiteName, baseData.fromSiteStoreNumber),
        48,
        this.summaryTitleFontSize,
      ),
      site_to_name: truncationText(
        generateSpacesText(baseData.name, baseData.store_number),
        50,
        this.summaryTitleFontSize,
      ),
      print_date: this.companyUtilService.convertUTCDateWithTimeZone(
        toDate().toISOString(),
        true,
        TIME_FORMATS.L,
      ),
    };
    // deal the inventoryItems data
    const siteIdTo = get(baseData, 'id');
    const mergedInventoryItems = this.getInventoryItemsBySiteToId(siteIdTo);
    set(document, 'inventory_items_list', mergedInventoryItems);

    return document;
  }

  public getInventoryItemsBySiteToId(siteIdTo: ObjectId): IInventoryItem[] {
    return map(
      filter(
        this.inventoryItemGroupResponse.items,
        (inventoryItem: IInventoryItem) =>
          isEqual(inventoryItem.site_id, siteIdTo),
      ),
      (inventoryItem: IInventoryItem) => {
        return {
          ...inventoryItem,
          par: this.getParString(
            inventoryItem.thresholds,
            inventoryItem.measures,
          ),
          measures: this.filterTransferUnitMeasures(inventoryItem),
        };
      },
    );
  }

  public filterTransferUnitMeasures(
    inventoryItem: IInventoryItem,
  ): InventoryItemVenueMeasure[] {
    const measures =
      map(inventoryItem.measures, (measure) => {
        measure.value = measure.entity_id;

        return measure;
      }) || [];
    const transferMeasures = filter(
      measures,
      (item) => item.transactions.transfer,
    );
    // sort the transfer Unit by description
    const sortMeasures = sortBy(transferMeasures, (item) =>
      toLower(item.description),
    );

    return sortMeasures;
  }

  public getParString(
    thresholds: Threshold[],
    measures: InventoryItemVenueMeasure[],
  ): string {
    const translatedEach = this.i18nService.translate(I18n.Common.Each);
    // this method logic is according to XVI-6440 TC
    if (isNil(thresholds) || isEmpty(thresholds)) {
      return '';
    }
    const defaultThreshold = find(thresholds, (threshold: Threshold) =>
      isEqual(threshold.type, 'default'),
    );
    if (isNil(defaultThreshold) || isNil(defaultThreshold.par_qty)) {
      return '';
    }
    const defaultMeasure = find(
      measures,
      (measure: InventoryItemVenueMeasure) =>
        isEqual(measure.entity_id, defaultThreshold.measure_entity_id),
    );
    if (isNil(defaultMeasure)) {
      return '';
    }
    const defaultMeasureText = generateUnitTextV2(defaultMeasure, {
      numberFormatService: this.numberFormatService,
      measureTransactionType: MEASURE_TRANSACTION.Transfer,
      notNeedAddSign: true,
    });
    const prefixPar = `${this.numberFormatService.getNumberFormatter(false)({
      value: defaultThreshold.par_qty,
    })} ${defaultMeasureText}`;
    if (isEqual(defaultMeasure?.factor_value, 1)) {
      return prefixPar;
    }
    const measuresFactorValueIsOne = filter(
      measures,
      (measure: InventoryItemVenueMeasure) => isEqual(measure?.factor_value, 1),
    );
    const measuresDescriptionEach = find(
      measures,
      (measure: InventoryItemVenueMeasure) =>
        isEqual(measure.description, 'Each'),
    );
    const convertedValue = new DecimalNumber(defaultThreshold.par_qty)
      .mul(defaultMeasure?.factor_value)
      .div(measuresDescriptionEach?.factor_value || 1);
    if (isEmpty(measuresFactorValueIsOne) && isNil(measuresDescriptionEach)) {
      return `${prefixPar}/${this.numberFormatService.getNumberFormatter(false)(
        {
          value: convertedValue,
        },
      )} ${translatedEach}[1]`;
    }
    if (isEmpty(measuresFactorValueIsOne) && !isNil(measuresDescriptionEach)) {
      return isEqual(
        `${prefixPar}`,
        `${this.numberFormatService.getNumberFormatter(false)({
          value: defaultThreshold.par_qty,
        })} ${translatedEach}[${measuresDescriptionEach?.factor_value}]`,
      )
        ? `${prefixPar}`
        : `${prefixPar}/${this.numberFormatService.getNumberFormatter(false)({
            value: convertedValue,
          })} ${translatedEach}[${measuresDescriptionEach?.factor_value}]`;
    }
    if (!isEmpty(measuresFactorValueIsOne)) {
      const measureValueDescription: string[] = map(
        measuresFactorValueIsOne,
        (measureValue) =>
          `/${this.numberFormatService.getNumberFormatter(false)({
            value: new DecimalNumber(defaultThreshold.par_qty).mul(
              defaultMeasure?.factor_value,
            ),
          })} ${measureValue.description}[1]`,
      );

      return isEqual(`/${prefixPar}`, measureValueDescription.join(''))
        ? `${prefixPar}`
        : `${prefixPar}${measureValueDescription.join('')}`;
    }
  }

  public listModalGridAction(
    event: XuxGridAction,
    grid: XuxGridListComponent,
  ): void {
    switch (event.type) {
      case XUX_GRID_ACTIONS.GRID_READY:
        this.siteModalSelected = 0;
        this.siteModalTotal = event.payload.gridApi.getDisplayedRowCount();
        this.siteModalDescription$.next(
          this.getSitesListText(
            this.siteModalSelected,
            this.siteModalTotal,
            I18n.EntityNames.SiteSingularAndPlural,
          ),
        );
        break;
      case XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED:
        this.siteModalSelected = 0;
        this.siteModalTotal = event.payload?.rowData?.length || 0;
        this.siteModalDescription$.next(
          this.getSitesListText(
            this.siteModalSelected,
            this.siteModalTotal,
            I18n.EntityNames.SiteSingularAndPlural,
          ),
        );
        break;
      case XUX_GRID_ACTIONS.ROW_SELECTION_CHANGED:
        this.siteModalSelected = event.payload?.rowData?.length || 0;
        this.siteModalDescription$.next(
          this.getSitesListText(
            this.siteModalSelected,
            this.siteModalTotal,
            I18n.EntityNames.SiteSingularAndPlural,
          ),
        );
        break;
      case XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED:
        this.siteModalSelected = event.payload?.rowData?.length || 0;
        this.setSubtitleForModel(grid);
        break;
      case XUX_GRID_ACTIONS.FILTER_MODEL_CHANGED:
        this.setSubtitleForModel(grid);
        break;
      default:
        return;
    }
  }

  public getSitesListText(
    siteListSelected: number,
    siteListTotal: number,
    entity: string,
  ): string {
    return isEqual(siteListSelected, 0)
      ? this.i18nService.instant(I18n.Forms.GridMessages.ShowingEntities, {
          count: siteListTotal,
          entities: this.i18nService.translate(entity),
        })
      : this.i18nService.instant(
          I18n.Forms.GridMessages.ShowingEntitiesWithSelected,
          {
            count: siteListTotal,
            entities: this.i18nService.translate(entity),
            selectedCount: siteListSelected,
          },
        );
  }

  public setSubtitleForModel(grid: XuxGridListComponent): void {
    this.subscriptions.push(
      grid.visibleItemCount$.subscribe((totalCountVisible: number) => {
        this.siteModalTotal = totalCountVisible;
        this.siteModalDescription$.next(
          this.getSitesListText(
            this.siteModalSelected,
            this.siteModalTotal,
            I18n.EntityNames.SiteSingularAndPlural,
          ),
        );
      }),
    );
  }

  public getToSiteListRequireValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlRoot = control.root;
      const controlParent = control.parent;

      if (isNil(controlRoot.value) || isNil(controlParent)) return null;

      return !isEmpty(control.value)
        ? null
        : {
            error: I18n.ErrorMessages.TransferNoSelectedToSitesError,
          };
    };
  }

  public async fetchGetDocumentById(
    documentId: string,
    options: IDocumentOperationOptions = {},
  ): Promise<ITransferGroupInfo> {
    return this.apiActions.fetchGetDocumentById(entityType, documentId, {
      ...options,
      query: {
        ...options.query,
      },
    });
  }

  public addAdditionalInfoToTransfer(
    transferItems: ITransferData[],
    groupName: string,
    reallyChangedItems?: Pick<ITransferData, 'entity_id'> &
      Partial<ITransferData>[],
  ): ITransferData[] {
    return map(transferItems, (item) => {
      return {
        ...item,
        virtual: {
          transfer_group_name: groupName,
          really_changed_transfers: reallyChangedItems,
        },
      };
    });
  }
  // eslint-disable-next-line max-lines
}
